import React, { useState, useEffect } from 'react';
import { AdminUserService, PendingUser } from '../services/adminUserService';
import { storage } from '../services/storage';
import { UserDetailModal } from '../components/admin/UserDetailModal';
import { UserDeletionModal } from '../components/admin/UserDeletionModal';
import { VerifiedStatePill, VerifiedState, paysGroupForBucket, paysSortRank } from '../components/admin/VerifiedStatePill';
import { Check, X, Trash2, GraduationCap, RefreshCw, Smartphone, Clipboard, Unlock, AlertTriangle } from 'lucide-react';

import { API_BASE_URL } from '../utils/api'; // Centralized environment-aware API URL
import { UserPaidAccessControls } from '../components/admin/UserPaidAccessControls';
import { ReadOnlyNote, useAdminRole } from './admin/partners/PartnersShared';

interface WaitlistEntry {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  signupSource: 'website' | 'referral' | 'manual';
  status: 'waiting' | 'invited' | 'converted';
  signupDate: string;
  adminNotes: string;
  referralCode?: string;
}

/**
 * FIX-UNITS-2 item 7: the athlete's canonical units as the admin API serves them
 * (services/unit_preferences.admin_units_view) — an object while
 * FF_UNITS_CANONICAL_RESOLVER is on, the legacy string while it is off, and
 * null ("no data") for an athlete with no core profile row.
 */
interface AdminUnitsView {
  distance: 'km' | 'miles';
  weight: 'kg' | 'lbs' | 'stones';
  temperature: 'celsius' | 'fahrenheit';
  height: 'cm' | 'ft_in';
  sources: Record<string, string>;
}

interface ExtendedUser extends Omit<PendingUser, 'status'> {
  status?: 'pending' | 'registered' | 'expired' | 'active' | 'suspended';
  preferredName?: string;
  profile?: {
    age?: number;
    height?: string;
    weight?: string;
    gender?: string;
    units?: AdminUnitsView | string | null;
    timezone?: string;
  };
  agreements?: {
    betaTerms?: boolean;
    privacyPolicy?: boolean;
    betaAcceptedAt?: string;
    hasFullAccess?: boolean;
  };
  engagement?: {
    messageCount?: number;
    lastMessageAt?: string;
  };
  subscription?: {
    userClass?: string;
    tier?: string;
    status?: string;
    updatedAt?: string;
    trialExpiresAt?: string | null;
    subscriptionEndsAt?: string | null;
    lastCancellationAt?: string | null;
    adminTierOverride?: string | null;
    evaluationType?: string | null;
    /** Summer50 — joined by redeeming another member's referral code. */
    joinedViaReferral?: boolean;
    verifiedState?: VerifiedState | null;
  };
  adminNotes?: string;
  deviceBound?: boolean;
  lastActiveAt?: string;
  // v39 (2026-06-03, migration 1609): per-user client telemetry from the
  // ClientTelemetryTracker middleware. NULL until the user makes an
  // authed request from a build that sends X-Client-* headers.
  lastSeenPlatform?: 'ios' | 'android' | 'web' | string | null;
  lastSeenAppVersion?: string | null;
  lastSeenAppBuild?: string | null;
  lastSeenAt?: string | null;
}

export const EnhancedAdminUserManagement: React.FC = () => {
  // Granting or revoking paid access (tier overrides, evaluations) is admin / super admin only:
  // the server answers 403 to support and viewer (Codes PR-1b, L4 / L-1), so their sessions see
  // each user's access as text, not a select (review N-e). Fail closed while the role is unknown.
  const { role, writable: canChangeAccess, loaded: roleLoaded } = useAdminRole();
  const [allUsers, setAllUsers] = useState<ExtendedUser[]>([]);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'pending' | 'active' | 'registered' | 'beta' | 'waitlist'>('all');
  // Secondary filter — the "Pays?" axis. Independent of the status tabs above
  // so admins can scan e.g. "all active users who are actually paying me."
  // Replaces no equivalent today; you used to have to eyeball the badge stack.
  const [paysFilter, setPaysFilter] = useState<'all' | 'paying' | 'trial' | 'comp' | 'churned'>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    adminNotes: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [selectedUserAgreement, setSelectedUserAgreement] = useState<any>(null);
  const [userStats, setUserStats] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  
  // UserDetailModal state
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  
  // UserDeletionModal state
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletionUser, setDeletionUser] = useState<{ id: string; email: string; name: string } | null>(null);

  // Column sort state for the user list table.
  // Activity column sorts by max(lastActiveAt, lastMessageAt) — picking up the new
  // last_active_at signal (any backend hit) added in the analytics-included sprint,
  // and falling back to last chat message for legacy users.
  type SortColumn = 'status' | 'name' | 'email' | 'activity' | 'sub' | 'role' | 'created';
  type SortDirection = 'asc' | 'desc';
  const [sortColumn, setSortColumn] = useState<SortColumn>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Waitlist state
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [waitlistSearch, setWaitlistSearch] = useState('');
  const [waitlistStatusFilter, setWaitlistStatusFilter] = useState<'all' | 'waiting' | 'invited' | 'converted'>('all');
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [showAddWaitlist, setShowAddWaitlist] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkPromote, setShowBulkPromote] = useState(false);
  const [isWaitlistLoading, setIsWaitlistLoading] = useState(false);
  const [newWaitlistEntry, setNewWaitlistEntry] = useState({ firstName: '', lastName: '', email: '', adminNotes: '', signupSource: 'manual' as const });
  const [promoteModalEntry, setPromoteModalEntry] = useState<WaitlistEntry | null>(null);
  const [promoteAuthMethod, setPromoteAuthMethod] = useState<'invite' | 'google' | 'email'>('invite');

  // Evaluation grant modal state
  type EvalModalMode = 'grant' | 'extend' | 'revoke' | null;
  type EvalStep = 'reason' | 'duration' | 'preflight' | 'confirm' | 'result';
  const [evalModalMode, setEvalModalMode] = useState<EvalModalMode>(null);
  const [evalStep, setEvalStep] = useState<EvalStep>('reason');
  const [evalUserId, setEvalUserId] = useState<string>('');
  const [evalUserName, setEvalUserName] = useState<string>('');
  const [evalUserEmail, setEvalUserEmail] = useState<string>('');
  const [evalUserSubscription, setEvalUserSubscription] = useState<any>(null);
  const [evalReason, setEvalReason] = useState<string>('');
  const [evalDuration, setEvalDuration] = useState<string>('');
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalError, setEvalError] = useState<string>('');
  const [evalResult, setEvalResult] = useState<any>(null);
  const [evalPreflight, setEvalPreflight] = useState<any>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showCreateForm || showAgreementModal || showUserDetail || showDeletionModal || evalModalMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCreateForm, showAgreementModal, showUserDetail, showDeletionModal, evalModalMode]);

  useEffect(() => {
    loadAllUsers();
    loadPendingUsers(); // Keep for backward compatibility
    loadWaitlistEntries();
    // Auto-refresh every 30 seconds for real-time status updates
    const interval = setInterval(() => {
      loadAllUsers();
      loadPendingUsers();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAllUsers = async () => {
    try {
      setIsLoading(true);
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      
      const response = await fetch(`${API_BASE}/users/all?include_status=${activeFilter}`, {
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
        setUserStats(data.stats || {});
      } else {
        console.warn('Enhanced API not available, falling back to legacy');
        // Fallback to legacy pending users
        const users = await AdminUserService.getPendingUsers();
        setAllUsers(users.map(u => ({ ...u, status: u.status || 'pending' })));
      }
    } catch (error) {
      console.error('Failed to load all users:', error);
      // Fallback to legacy API
      try {
        const users = await AdminUserService.getPendingUsers();
        setAllUsers(users.map(u => ({ ...u, status: u.status || 'pending' })));
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    try {
      const users = await AdminUserService.getPendingUsers();
      setPendingUsers(users);
    } catch (error) {
      console.error('Failed to load pending users:', error);
    }
  };

  const loadWaitlistEntries = async () => {
    setIsWaitlistLoading(true);
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      const response = await fetch(`${API_BASE}/waitlist`, {
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWaitlistEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Failed to load waitlist entries:', error);
    } finally {
      setIsWaitlistLoading(false);
    }
  };

  const promoteWaitlistEntry = (entry: WaitlistEntry) => {
    setPromoteModalEntry(entry);
    setPromoteAuthMethod('invite');
  };

  const confirmPromoteWaitlistEntry = async () => {
    const entry = promoteModalEntry;
    if (!entry) return;
    setPromoteModalEntry(null);
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      const response = await fetch(`${API_BASE}/waitlist/${entry.id}/promote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authMethod: promoteAuthMethod,
          sendWelcomeEmail: true
        })
      });
      if (response.ok) {
        await loadWaitlistEntries();
        alert(`${entry.email} has been graduated and will receive a welcome email.`);
      } else {
        const err = await response.json();
        alert(`Failed to promote: ${err.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to promote waitlist entry:', error);
      alert('Failed to promote entry. Please try again.');
    }
  };

  const bulkPromoteWaitlistEntries = async () => {
    const ids = Array.from(selectedEntries);
    if (ids.length === 0) return;
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      let successCount = 0;
      for (const id of ids) {
        const response = await fetch(`${API_BASE}/waitlist/${id}/promote`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) successCount++;
      }
      setSelectedEntries(new Set());
      setShowBulkPromote(false);
      await loadWaitlistEntries();
      alert(`Successfully graduated ${successCount} of ${ids.length} waitlist entries.`);
    } catch (error) {
      console.error('Failed to bulk promote:', error);
      alert('Bulk promotion failed. Please try again.');
    }
  };

  const deleteWaitlistEntry = async (id: string, email: string) => {
    if (!confirm(`Delete waitlist entry for ${email}?`)) return;
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      const response = await fetch(`${API_BASE}/waitlist/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
        }
      });
      if (response.ok) {
        await loadWaitlistEntries();
      } else {
        alert('Failed to delete entry.');
      }
    } catch (error) {
      console.error('Failed to delete waitlist entry:', error);
      alert('Failed to delete entry. Please try again.');
    }
  };

  const bulkDeleteWaitlistEntries = async () => {
    const ids = Array.from(selectedEntries);
    if (ids.length === 0) return;
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      let successCount = 0;
      for (const id of ids) {
        const response = await fetch(`${API_BASE}/waitlist/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          }
        });
        if (response.ok) successCount++;
      }
      setSelectedEntries(new Set());
      setShowBulkDelete(false);
      await loadWaitlistEntries();
      alert(`Deleted ${successCount} entries.`);
    } catch (error) {
      console.error('Failed to bulk delete:', error);
      alert('Bulk delete failed. Please try again.');
    }
  };

  const addWaitlistEntry = async () => {
    if (!newWaitlistEntry.email) {
      alert('Email is required.');
      return;
    }
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      const response = await fetch(`${API_BASE}/waitlist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newWaitlistEntry)
      });
      if (response.ok) {
        setNewWaitlistEntry({ firstName: '', lastName: '', email: '', adminNotes: '', signupSource: 'manual' });
        setShowAddWaitlist(false);
        await loadWaitlistEntries();
      } else {
        const err = await response.json();
        alert(`Failed to add entry: ${err.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to add waitlist entry:', error);
      alert('Failed to add entry. Please try again.');
    }
  };

  const getFilteredWaitlist = () => {
    return waitlistEntries.filter(entry => {
      const matchesSearch = !waitlistSearch ||
        entry.email.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
        (entry.firstName || '').toLowerCase().includes(waitlistSearch.toLowerCase()) ||
        (entry.lastName || '').toLowerCase().includes(waitlistSearch.toLowerCase());
      const matchesStatus = waitlistStatusFilter === 'all' || entry.status === waitlistStatusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email) {
      alert('Please fill in all fields');
      return;
    }

    setIsGenerating(true);
    try {
      // Try enhanced API first
      try {
        const result = await AdminUserService.createUser(newUser);
        await loadAllUsers();
        await loadPendingUsers();
        setNewUser({ firstName: '', lastName: '', email: '', adminNotes: '' });
        setShowCreateForm(false);
        alert(`User created successfully! Invite URL: ${result.inviteUrl}`);
      } catch (enhancedError) {
        // Fallback to legacy API
        const result = await AdminUserService.createPendingUser(newUser);
        setPendingUsers(prev => [result, ...prev]);
        await loadPendingUsers();
        setNewUser({ firstName: '', lastName: '', email: '', adminNotes: '' });
        setShowCreateForm(false);
        alert(`User created successfully! Invite URL: ${result.inviteUrl}`);
      }
    } catch (error) {
      console.error('Failed to create user:', error);
      alert('Failed to create user. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (url: string, userId: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(userId);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const revokeInvite = async (userId: string) => {
    if (!confirm('Are you sure you want to revoke this invite?')) return;
    
    try {
      await AdminUserService.revokeInvite(userId);
      setPendingUsers(prev => prev.filter(user => user.id !== userId));
      setAllUsers(prev => prev.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Failed to revoke invite:', error);
    }
  };

  const regenerateInvite = async (userId: string) => {
    try {
      const newInvite = await AdminUserService.regenerateInvite(userId);
      setPendingUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, ...newInvite } : user
      ));
      setAllUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, ...newInvite } : user
      ));
    } catch (error) {
      console.error('Failed to regenerate invite:', error);
    }
  };

  const resetDeviceBinding = async (userId: string, token: string) => {
    if (!confirm('Reset device binding for this invite? This will allow it to be used on a different device.')) return;
    
    try {
      const API_BASE = window.location.hostname === 'localhost' ?
        '/api/admin' : `${API_BASE_URL}/api/admin`;
      
      const response = await fetch(`${API_BASE}/users/${token}/reset-device`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await storage.getItem('admin_token') || 'admin-token'}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        loadAllUsers();
        alert('Device binding reset successfully! The invite can now be used on any device.');
      }
    } catch (error) {
      console.error('Failed to reset device binding:', error);
      alert('Failed to reset device binding. Please try again.');
    }
  };

  const EVALUATION_DURATION_OPTIONS: Record<string, string> = {
    'three_day':    '3 days',
    'weekly':       '1 week',
    'monthly':      '1 month',
    'two_month':    '2 months',
    'three_month':  '3 months',
    'six_month':    '6 months',
    'yearly':       '1 year',
    'lifetime':     'Lifetime',
  };

  const EVALUATION_REASON_OPTIONS: { key: string; label: string; description: string; defaultDuration: string }[] = [
    { key: 'platform_issue',      label: 'Platform Issue',        description: 'Bug or service issue impacted the user experience', defaultDuration: 'two_month' },
    { key: 'early_adopter',       label: 'Early Adopter',         description: 'Reward for early platform support and engagement', defaultDuration: 'two_month' },
    { key: 'extended_trial',      label: 'Extended Trial',        description: 'User needs more time to evaluate the platform', defaultDuration: 'monthly' },
    { key: 'feedback',            label: 'Feedback Contributor',  description: 'Provided valuable product feedback or testing', defaultDuration: 'three_month' },
    { key: 'onboarding_recovery', label: 'Onboarding Recovery',   description: 'Onboarding was disrupted — give them a fresh start', defaultDuration: 'monthly' },
  ];

  // ── Evaluation Modal Helpers ──

  const openEvalModal = (mode: 'grant' | 'extend' | 'revoke', user: ExtendedUser) => {
    const userName = user.preferredName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
    setEvalModalMode(mode);
    setEvalUserId(user.id);
    setEvalUserName(userName);
    setEvalUserEmail(user.email);
    setEvalUserSubscription(user.subscription || null);
    setEvalReason('');
    setEvalDuration('');
    setEvalError('');
    setEvalResult(null);
    setEvalPreflight(null);
    setEvalLoading(false);

    if (mode === 'grant') {
      setEvalStep('reason');
    } else if (mode === 'extend') {
      setEvalStep('duration');
    } else if (mode === 'revoke') {
      setEvalStep('confirm');
    }
  };

  const closeEvalModal = () => {
    if (evalLoading) return; // Don't close while an operation is in flight
    setEvalModalMode(null);
  };

  const evalStepIndex = (): number => {
    if (evalModalMode === 'grant') {
      const steps: EvalStep[] = ['reason', 'duration', 'preflight', 'confirm', 'result'];
      return steps.indexOf(evalStep) + 1;
    }
    if (evalModalMode === 'extend') {
      const steps: EvalStep[] = ['duration', 'confirm', 'result'];
      return steps.indexOf(evalStep) + 1;
    }
    if (evalModalMode === 'revoke') {
      const steps: EvalStep[] = ['confirm', 'result'];
      return steps.indexOf(evalStep) + 1;
    }
    return 0;
  };

  const evalTotalSteps = (): number => {
    if (evalModalMode === 'grant') return 5;
    if (evalModalMode === 'extend') return 3;
    if (evalModalMode === 'revoke') return 2;
    return 0;
  };

  const evalGoBack = () => {
    setEvalError('');
    if (evalModalMode === 'grant') {
      if (evalStep === 'duration') setEvalStep('reason');
      else if (evalStep === 'preflight') setEvalStep('duration');
      else if (evalStep === 'confirm') setEvalStep('preflight');
    } else if (evalModalMode === 'extend') {
      if (evalStep === 'confirm') setEvalStep('duration');
    }
  };

  const evalSelectReason = (reasonKey: string) => {
    const r = EVALUATION_REASON_OPTIONS.find(o => o.key === reasonKey);
    setEvalReason(reasonKey);
    setEvalDuration(r?.defaultDuration || 'two_month');
    setEvalError('');
    setEvalStep('duration');
  };

  const evalSelectDuration = (durationKey: string) => {
    setEvalDuration(durationKey);
    setEvalError('');
    if (evalModalMode === 'grant') {
      setEvalStep('preflight');
      runPreflight(durationKey);
    } else if (evalModalMode === 'extend') {
      setEvalStep('confirm');
    }
  };

  const runPreflight = async (selectedDuration?: string) => {
    setEvalLoading(true);
    setEvalError('');
    try {
      const result = await AdminUserService.evaluationPreflight(evalUserId);

      // Use passed duration to avoid stale state from React batching
      const effectiveDuration = selectedDuration || evalDuration;
      const reasonConfig = EVALUATION_REASON_OPTIONS.find(r => r.key === evalReason);
      const campaignInfo = result.campaigns?.[evalReason] || {};

      setEvalPreflight({
        eligible: result.eligible,
        eligibilityNote: result.reason,
        currentTier: result.current_tier || 'none',
        currentStatus: result.current_status || 'unknown',
        currentClass: result.current_class || 'standard',
        willChangeTo: 'extended (Pro)',
        reason: reasonConfig?.label || evalReason,
        duration: EVALUATION_DURATION_OPTIONS[effectiveDuration] || effectiveDuration,
        deferredStart: result.deferred_start || false,
        campaignTrigger: `evaluation_${evalReason}`,
        campaignReady: campaignInfo.campaign_ready || false,
        campaignStepCount: campaignInfo.step_count || 0,
        emailWillSend: true,
      });
    } catch (err: any) {
      setEvalError(err?.message || 'Failed to run preflight check');
    }
    setEvalLoading(false);
  };

  const executeGrant = async () => {
    setEvalLoading(true);
    setEvalError('');
    try {
      const result = await AdminUserService.grantEvaluation(evalUserId, evalReason, evalDuration);
      setEvalResult({ success: true, ...result });
      setEvalStep('result');
      await loadAllUsers();
    } catch (error: any) {
      setEvalError(error?.message || 'Failed to grant evaluation. User may not be eligible.');
    }
    setEvalLoading(false);
  };

  const executeExtend = async () => {
    setEvalLoading(true);
    setEvalError('');
    try {
      const result = await AdminUserService.extendEvaluation(evalUserId, evalDuration);
      setEvalResult({ success: true, ...result });
      setEvalStep('result');
      await loadAllUsers();
    } catch (error: any) {
      setEvalError(error?.message || 'Failed to extend evaluation.');
    }
    setEvalLoading(false);
  };

  const executeRevoke = async () => {
    setEvalLoading(true);
    setEvalError('');
    try {
      const result = await AdminUserService.revokeEvaluation(evalUserId);
      setEvalResult({ success: true, ...result });
      setEvalStep('result');
      await loadAllUsers();
    } catch (error: any) {
      setEvalError(error?.message || 'Failed to revoke evaluation.');
    }
    setEvalLoading(false);
  };

  // Legacy handlers — now just open the modal
  const handleGrantEvaluation = (userId: string, userName: string) => {
    const user = allUsers.find(u => u.id === userId);
    if (!user) { alert(`User ${userName} not found. Try refreshing the page.`); return; }
    openEvalModal('grant', user);
  };

  const handleRevokeEvaluation = (userId: string, userName: string) => {
    const user = allUsers.find(u => u.id === userId);
    if (!user) { alert(`User ${userName} not found. Try refreshing the page.`); return; }
    openEvalModal('revoke', user);
  };

  const handleExtendEvaluation = (userId: string, userName: string) => {
    const user = allUsers.find(u => u.id === userId);
    if (!user) { alert(`User ${userName} not found. Try refreshing the page.`); return; }
    openEvalModal('extend', user);
  };

  const handleTierOverride = async (userId: string, userName: string, overrideTier: string | null) => {
    const labels: Record<string, string> = {
      '': 'Standard (no override)',
      'basic': 'Essentials Tester',
      'full': 'Chat+ Tester',
      'extended': 'Pro Tester',
      'unlimited': 'Unlimited Tester',
    };
    const label = labels[overrideTier || ''] || 'Standard';
    // Open a simple confirmation via the eval modal pattern
    if (!window.confirm(`Set ${userName} to "${label}"?`)) return;
    try {
      await AdminUserService.setTierOverride(userId, overrideTier, `Set to ${label} via admin portal`);
      await loadAllUsers();
    } catch (error) {
      console.error('Failed to set tier override:', error);
      alert('Failed to set tier override. Please try again.');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'registered':
        return <span className="text-green-500"><Check size={14} /></span>;
      case 'expired':
        return <span className="text-red-500"><X size={14} /></span>;
      default:
        return <span className="text-yellow-500">⏳</span>;
    }
  };

  const formatExpiryTime = (expiresAt: Date) => {
    const now = new Date();
    const timeLeft = new Date(expiresAt).getTime() - now.getTime();
    
    if (timeLeft <= 0) return 'Expired';
    
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    
    return `${hours}h ${minutes}m`;
  };

  const showAgreementDetails = async (user: ExtendedUser) => {
    try {
      const agreementInfo = {
        user: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email
        },
        signature: {
          signed: user.agreements?.betaTerms || user.status === 'registered',
          signatureDate: user.agreements?.betaAcceptedAt || (user.status === 'registered' ? '2025-01-15T10:30:00Z' : null),
          agreementVersion: '2025-06-24-BETA3',
          ipAddress: '192.168.1.100',
          browser: 'Chrome 131.0.0.0',
          device: 'Desktop - macOS',
          location: 'Toronto, ON, Canada'
        }
      };
      
      setSelectedUserAgreement(agreementInfo);
      setShowAgreementModal(true);
    } catch (error) {
      console.error('Failed to load agreement details:', error);
    }
  };

    // Pull a comparable value off a user for the chosen sort column.
    // Returns null when the field is genuinely empty so the sort can push those
    // rows to the end regardless of direction (NULLS LAST behavior).
    const getSortValue = (u: ExtendedUser, col: SortColumn): string | number | null => {
      switch (col) {
        case 'status':
          return u.status || '';
        case 'name':
          return (u.preferredName || `${u.firstName || ''} ${u.lastName || ''}`).trim().toLowerCase();
        case 'email':
          return (u.email || '').toLowerCase();
        case 'activity': {
          // "Last activity date" = max of any backend hit (last_active_at, set by
          // UserActivityTracker middleware) and last chat message (legacy fallback).
          const a = u.lastActiveAt ? new Date(u.lastActiveAt).getTime() : null;
          const m = u.engagement?.lastMessageAt ? new Date(u.engagement.lastMessageAt).getTime() : null;
          if (a === null && m === null) return null;
          return Math.max(a ?? 0, m ?? 0);
        }
        case 'sub': {
          // Sort by verified bucket priority (paying first, churned last) so
          // an ascending sort surfaces revenue at the top of the list.
          // Falls back to tier string for users with no verifiedState (legacy
          // clients reading an older API response).
          const bucket = u.subscription?.verifiedState?.bucket;
          if (bucket) return paysSortRank(bucket);
          return u.subscription?.tier ? 100 : null;
        }
        case 'role':
          return u.subscription?.userClass || '';
        case 'created':
          return u.createdAt ? new Date(u.createdAt).getTime() : null;
        default:
          return null;
      }
    };

    const handleSort = (col: SortColumn) => {
      if (col === sortColumn) {
        setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(col);
        // Default to descending for time/numeric columns (most-recent first feels right);
        // ascending for text columns (A→Z).
        setSortDirection(col === 'created' || col === 'activity' ? 'desc' : 'asc');
      }
    };

    const getFilteredUsers = () => {
      let filtered: ExtendedUser[];
      if (activeFilter === 'all') filtered = allUsers;
      else if (activeFilter === 'pending') filtered = allUsers.filter(u => u.status === 'pending');
      else if (activeFilter === 'active') filtered = allUsers.filter(u => u.status === 'active');
      else if (activeFilter === 'registered') filtered = allUsers.filter(u => u.status === 'registered');
      else if (activeFilter === 'beta') filtered = allUsers.filter(u => u.subscription?.userClass === 'beta_tester');
      else filtered = allUsers;

      // Secondary "Pays?" filter. Independent of the status filter above —
      // applied as an AND so "Active + Paying" shows users who are both
      // logged-in active accounts AND actively giving us money.
      if (paysFilter !== 'all') {
        filtered = filtered.filter(u => {
          const bucket = u.subscription?.verifiedState?.bucket;
          if (!bucket) return paysFilter === 'churned' && !u.subscription?.tier;
          return paysGroupForBucket(bucket) === paysFilter;
        });
      }

      // Stable sort copy (Array.prototype.sort is in-place; we don't want to mutate state).
      const sorted = [...filtered].sort((a, b) => {
        const av = getSortValue(a, sortColumn);
        const bv = getSortValue(b, sortColumn);
        // NULLS LAST regardless of direction.
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        if (av < bv) return sortDirection === 'asc' ? -1 : 1;
        if (av > bv) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    };

  return (
    <div className="space-y-6">
      <ReadOnlyNote writable={canChangeAccess} loaded={roleLoaded} role={role}
                    subject="anyone's paid access (tier overrides and evaluations)" />
      {/* Stats Dashboard */}
      <div className="bg-white p-5" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
        <h2 className="text-sm font-semibold mb-3" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--text-primary)' }}>User Statistics</h2>
        <div className="grid grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{userStats.total || allUsers.length}</div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Total Users</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: 'var(--status-warning)' }}>{userStats.pending || allUsers.filter(u => u.status === 'pending').length}</div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Pending</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: 'var(--cedar-primary)' }}>{userStats.active || allUsers.filter(u => u.status === 'active').length}</div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Active</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: 'var(--cedar-stream)' }}>{userStats.loggedIn || allUsers.filter(u => u.firstLoginAt).length}</div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Logged In</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: 'var(--status-error)' }}>{userStats.suspended || 0}</div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Suspended</div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      {/* v41 (2026-06-08): dropped equal-width division from each tab
          button + added flex-wrap on the container. Previously the
          stretch class forced equal-width across 6 chips, which
          compressed them into illegible slivers at narrow viewports.
          Natural-width buttons + wrap = readable chips that flow to a
          2nd row when needed. */}
      <div className="flex flex-wrap gap-1" style={{ padding: '4px', backgroundColor: 'var(--cedar-limestone)', borderRadius: '10px' }}>
        {[
          { key: 'all' as const, label: `All Users (${allUsers.length})`, onClick: () => { setActiveFilter('all'); loadAllUsers(); } },
          { key: 'pending' as const, label: `Pending (${allUsers.filter(u => u.status === 'pending').length})`, onClick: () => setActiveFilter('pending') },
          { key: 'active' as const, label: `Active (${allUsers.filter(u => u.status === 'active').length})`, onClick: () => setActiveFilter('active') },
          { key: 'registered' as const, label: `Registered (${allUsers.filter(u => u.status === 'registered').length})`, onClick: () => setActiveFilter('registered') },
          { key: 'beta' as const, label: `Beta (${allUsers.filter(u => u.subscription?.userClass === 'beta_tester').length})`, onClick: () => setActiveFilter('beta') },
          { key: 'waitlist' as const, label: `Waitlist (${waitlistEntries.length})`, onClick: () => { setActiveFilter('waitlist'); loadWaitlistEntries(); } },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={tab.onClick}
            className="transition-all"
            style={{
              padding: '8px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '8px', border: 'none', cursor: 'pointer',
              fontFamily: "'League Spartan', sans-serif",
              ...(activeFilter === tab.key
                ? { backgroundColor: 'var(--cedar-primary)', color: 'var(--surface)', boxShadow: '0 1px 3px rgba(31,108,79,0.25)' }
                : { backgroundColor: 'transparent', color: 'var(--text-secondary)' }),
            }}
            onMouseEnter={(e) => { if (activeFilter !== tab.key) { e.currentTarget.style.backgroundColor = 'var(--cedar-subtle)'; e.currentTarget.style.color = 'var(--cedar-primary)'; } }}
            onMouseLeave={(e) => { if (activeFilter !== tab.key) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; } }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pays? secondary filter — only shown for user-list views (not waitlist).
          Layered under the status tabs so an admin can compose
          e.g. "Active + Paying" to scan revenue, or "All + Comp" to audit
          admin grants without writing SQL. Counts use verifiedState.bucket,
          falling back to 0 for users on a legacy /users/all response. */}
      {/* v43 (2026-06-08): was `marginTop: '-8px'` — a negative margin
          designed assuming the status tab row was always a single line.
          After v41 added flex-wrap to the status row, the Pays? row
          visually overlapped the bottom edge of the status tabs at
          certain widths. Switched to a small positive margin so the
          two rows always have breathing room. */}
      {activeFilter !== 'waitlist' && (
        <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: '8px' }}>
          <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)', fontFamily: "'League Spartan', sans-serif" }}>
            Pays?
          </span>
          {([
            { key: 'all' as const, label: 'All' },
            { key: 'paying' as const, label: 'Paying' },
            { key: 'trial' as const, label: 'Trial' },
            { key: 'comp' as const, label: 'Comp' },
            { key: 'churned' as const, label: 'Churned' },
          ]).map(chip => {
            const count = chip.key === 'all'
              ? allUsers.length
              : allUsers.filter(u => {
                  const bucket = u.subscription?.verifiedState?.bucket;
                  return bucket && paysGroupForBucket(bucket) === chip.key;
                }).length;
            const isActive = paysFilter === chip.key;
            return (
              <button
                key={chip.key}
                onClick={() => setPaysFilter(chip.key)}
                className="transition-all"
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  borderRadius: '999px',
                  border: isActive ? '1px solid var(--cedar-primary)' : '1px solid var(--border)',
                  backgroundColor: isActive ? 'var(--cedar-primary)' : 'var(--surface)',
                  color: isActive ? 'var(--surface)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontFamily: "'League Spartan', sans-serif",
                }}
              >
                {chip.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Waitlist Tab Content */}
      {activeFilter === 'waitlist' && (
        <div className="bg-white" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
          <div className="p-5">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Waitlist</h1>
                <p className="text-gray-600 mt-1">
                  {getFilteredWaitlist().length} entries
                </p>
              </div>
              <div className="flex space-x-3">
                {selectedEntries.size > 0 && (
                  <>
                    <button
                      onClick={() => setShowBulkPromote(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <GraduationCap size={16} style={{ display: 'inline', verticalAlign: '-3px', marginRight: 4 }} />Graduate Selected ({selectedEntries.size})
                    </button>
                    <button
                      onClick={() => setShowBulkDelete(true)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Trash2 size={16} style={{ display: 'inline', verticalAlign: '-3px', marginRight: 4 }} />Delete Selected ({selectedEntries.size})
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowAddWaitlist(true)}
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  + Add Entry
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200 flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={waitlistSearch}
              onChange={e => setWaitlistSearch(e.target.value)}
              className="border rounded-lg px-3 py-2 w-64"
            />
            <select
              value={waitlistStatusFilter}
              onChange={e => setWaitlistStatusFilter(e.target.value as 'all' | 'waiting' | 'invited' | 'converted')}
              className="border rounded-lg px-3 py-2"
            >
              <option value="all">All Statuses</option>
              <option value="waiting">Waiting</option>
              <option value="invited">Invited</option>
              <option value="converted">Converted</option>
            </select>
          </div>

          {/* Table */}
          {isWaitlistLoading ? (
            <div className="p-8 text-center text-gray-500">Loading waitlist...</div>
          ) : getFilteredWaitlist().length === 0 ? (
            <div className="p-8 text-center text-gray-500">No waitlist entries found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="p-4 text-left">
                      <input
                        type="checkbox"
                        checked={selectedEntries.size === getFilteredWaitlist().length && getFilteredWaitlist().length > 0}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedEntries(new Set(getFilteredWaitlist().map(entry => entry.id)));
                          } else {
                            setSelectedEntries(new Set());
                          }
                        }}
                      />
                    </th>
                    <th className="p-4 text-left text-sm font-medium text-gray-500">Name</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-500">Email</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-500">Joined</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-500">Source</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-500">Notes</th>
                    <th className="p-4 text-left text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredWaitlist().map(entry => (
                    <tr key={entry.id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid var(--cedar-limestone)' }}>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedEntries.has(entry.id)}
                          onChange={e => {
                            const next = new Set(selectedEntries);
                            if (e.target.checked) next.add(entry.id);
                            else next.delete(entry.id);
                            setSelectedEntries(next);
                          }}
                        />
                      </td>
                      <td className="p-4 font-medium">
                        {entry.firstName || ''} {entry.lastName || ''}
                        {!entry.firstName && !entry.lastName && <span className="text-gray-400 italic">No name</span>}
                      </td>
                      <td className="p-4 text-sm">{entry.email}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          entry.status === 'waiting' ? 'bg-yellow-100 text-yellow-800' :
                          entry.status === 'invited' ? 'bg-blue-100 text-blue-800' :
                          entry.status === 'converted' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {entry.status}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {entry.signupDate ? new Date(entry.signupDate).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-4 text-sm text-gray-600">{entry.signupSource || '—'}</td>
                      <td className="p-4 text-sm text-gray-600 max-w-xs truncate">{entry.adminNotes || '—'}</td>
                      <td className="p-4">
                        <div className="flex space-x-2">
                          {entry.status !== 'converted' && (
                            <button
                              onClick={() => promoteWaitlistEntry(entry)}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                            >
                              <GraduationCap size={14} style={{ display: 'inline', verticalAlign: '-3px', marginRight: 4 }} />Graduate
                            </button>
                          )}
                          <button
                            onClick={() => deleteWaitlistEntry(entry.id, entry.email)}
                            className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeFilter !== 'waitlist' && (
      <div className="bg-white" style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}>
        <div className="p-5">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-semibold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--text-primary)' }}>
                {activeFilter === 'all' ? 'All Users' :
                 activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1) + ' Users'}
              </h1>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                {getFilteredUsers().length} users
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCreateForm(true)}
                className="text-white px-3 py-1.5 rounded-md transition-colors text-xs" style={{ backgroundColor: 'var(--cedar-primary)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'; }}
              >
                + New User
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Users List */}
        <div className="px-5 pb-5">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="flex justify-center"><RefreshCw size={28} className="animate-spin text-gray-400" /></div>
              <p className="text-gray-600 mt-2">Loading users...</p>
            </div>
          ) : getFilteredUsers().length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No users found for the selected filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm" style={{ minWidth: '900px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--cedar-limestone)' }}>
                    {([
                      ['status', 'Status'],
                      ['name', 'Name'],
                      ['email', 'Email'],
                      ['activity', 'Activity'],
                      ['sub', 'Pays?'],
                      ['role', 'Role'],
                      ['created', 'Created'],
                    ] as Array<[SortColumn, string]>).map(([col, label]) => {
                      const active = sortColumn === col;
                      const arrow = active ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '';
                      return (
                        <th
                          key={col}
                          onClick={() => handleSort(col)}
                          className="text-left px-2 py-2 text-xs font-semibold select-none cursor-pointer hover:text-gray-700"
                          style={{ color: active ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
                          title={`Sort by ${label}`}
                        >
                          {label}{arrow}
                        </th>
                      );
                    })}
                    {/* v39 (2026-06-03, migration 1609): Platform + Version
                        columns. Populated by the ClientTelemetryTracker
                        middleware on every authed request that carries
                        X-Client-* headers. Native users populate after the
                        next EAS build; web users populate immediately. */}
                    <th
                      className="text-left px-2 py-2 text-xs font-semibold"
                      style={{ color: 'var(--text-tertiary)' }}
                      title="Last observed client platform (iOS/Android/Web). Stamped on every authed request via the X-Client-Platform header."
                    >
                      Platform
                    </th>
                    <th
                      className="text-left px-2 py-2 text-xs font-semibold"
                      style={{ color: 'var(--text-tertiary)' }}
                      title="Last observed app version + build. e.g. 1.3.10 (201)"
                    >
                      Version
                    </th>
                    <th className="text-left px-2 py-2 text-xs font-semibold" style={{ color: 'var(--text-tertiary)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredUsers().map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid var(--cedar-limestone)' }}>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' :
                          user.status === 'registered' ? 'bg-blue-100 text-blue-800' :
                          user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 font-medium text-sm">
                        {user.preferredName || `${user.firstName} ${user.lastName}`}
                        {user.deviceBound && (
                          <span className="ml-1 text-green-600" title="Device Bound"><Smartphone size={12} style={{ display: 'inline', verticalAlign: '-2px' }} /></span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-gray-600" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</td>
                      <td className="px-2 py-1.5 text-xs">
                        {(() => {
                          // "Last activity" = any backend hit (last_active_at) OR last chat message,
                          // whichever is more recent. last_active_at covers working out, viewing the
                          // plan, browsing — not just chat. Falls back to lastMessageAt for users from
                          // before the activity-tracker middleware shipped.
                          const aMs = user.lastActiveAt ? new Date(user.lastActiveAt).getTime() : 0;
                          const mMs = user.engagement?.lastMessageAt ? new Date(user.engagement.lastMessageAt).getTime() : 0;
                          const lastMs = Math.max(aMs, mMs);
                          const messageCount = user.engagement?.messageCount || 0;
                          if (!lastMs && !messageCount) {
                            return <span className="text-gray-400">No activity</span>;
                          }
                          return (
                            <div>
                              {/* v41 (2026-06-08): relabel 'exchanges' → 'messages' to match the
                                  new SQL semantics. Pre-v41 this counted assistant replies; now
                                  it counts user messages (role='user'). 'Exchanges' implied a
                                  back-and-forth shape, which it never actually measured. */}
                              {messageCount > 0 && <div>{messageCount} messages</div>}
                              {lastMs > 0 && (
                                <div className="text-xs text-gray-500">
                                  Last: {new Date(lastMs).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-1.5 text-xs">
                        {/* Single derived pill — one mutually exclusive bucket
                            with optional suffixes. The old stack of
                            tier + secondary state + eval chip + override
                            dropdown collapsed into one decision: is money
                            changing hands right now? See
                            services/subscription_verifier.py for the rules
                            and VerifiedStatePill.tsx for the visuals. */}
                        <VerifiedStatePill state={user.subscription?.verifiedState} compact />
                      </td>
                      <td className="px-2 py-1.5 text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          user.subscription?.userClass === 'beta_tester' ? 'bg-teal-100 text-teal-800' :
                          user.subscription?.userClass === 'admin' ? 'bg-indigo-100 text-indigo-800' :
                          'bg-gray-50 text-gray-500'
                        }`}>
                          {user.subscription?.userClass === 'beta_tester' ? 'Beta' :
                           user.subscription?.userClass === 'admin' ? 'Admin' :
                           'User'}
                        </span>
                        {/* Summer50 — this user joined through the referral
                            program (redeemed a member's CEDAR- code). Answers
                            "how many came from referrals?" at a glance. */}
                        {user.subscription?.joinedViaReferral && (
                          <span
                            className="ml-1 px-1.5 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800"
                            title="Joined via a friend's referral code"
                          >
                            Friend
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      {/* v39 (2026-06-03, migration 1609): Platform cell.
                          Shows iOS / Android / Web as a small badge.
                          Empty dash when no client telemetry yet. */}
                      <td className="px-2 py-1.5 text-xs">
                        {user.lastSeenPlatform ? (
                          <span
                            className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              user.lastSeenPlatform === 'ios' ? 'bg-gray-100 text-gray-800' :
                              user.lastSeenPlatform === 'android' ? 'bg-green-100 text-green-800' :
                              user.lastSeenPlatform === 'web' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-700'
                            }`}
                            title={user.lastSeenAt ? `Last seen: ${new Date(user.lastSeenAt).toLocaleString()}` : ''}
                          >
                            {user.lastSeenPlatform === 'ios' ? 'iOS' :
                             user.lastSeenPlatform === 'android' ? 'Android' :
                             user.lastSeenPlatform === 'web' ? 'Web' :
                             user.lastSeenPlatform}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      {/* v39 (2026-06-03, migration 1609): Version cell.
                          Shows "1.3.10 (201)" — version + build in parens.
                          Last-seen timestamp surfaces in title-tooltip. */}
                      <td className="px-2 py-1.5 text-xs text-gray-600">
                        {user.lastSeenAppVersion ? (
                          <span
                            title={user.lastSeenAt ? `Last seen: ${new Date(user.lastSeenAt).toLocaleString()}` : ''}
                          >
                            {user.lastSeenAppVersion}
                            {user.lastSeenAppBuild && user.lastSeenAppBuild !== user.lastSeenAppVersion
                              ? ` (${user.lastSeenAppBuild})`
                              : ''}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex space-x-1">
                          {user.status === 'pending' && user.inviteUrl && (
                            <button
                              onClick={() => copyToClipboard(user.inviteUrl, user.id)}
                              className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                              title="Copy invite link"
                            >
                              {copySuccess === user.id ? <Check size={14} /> : <Clipboard size={14} />}
                            </button>
                          )}
                          {user.deviceBound && (
                            <button
                              onClick={() => resetDeviceBinding(user.id, user.inviteToken)}
                              className="text-xs px-2 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                              title="Reset device binding"
                            >
                              <Unlock size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setShowUserDetail(true);
                            }}
                            className="text-xs px-2 py-1 text-white rounded transition-colors"
                            style={{ backgroundColor: 'var(--cedar-primary)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--cedar-dark)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--cedar-primary)'; }}
                            title="View details"
                          >
                            View
                          </button>
                          {user.status === 'pending' && (
                            <button
                              onClick={() => revokeInvite(user.id)}
                              className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                              title="Revoke"
                            >
                              <X size={14} />
                            </button>
                          )}
                          {user.status !== 'pending' && (
                            <UserPaidAccessControls
                              user={user}
                              userName={user.preferredName || user.email}
                              writable={canChangeAccess}
                              onGrantEvaluation={handleGrantEvaluation}
                              onExtendEvaluation={handleExtendEvaluation}
                              onRevokeEvaluation={handleRevokeEvaluation}
                              onTierOverride={handleTierOverride}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Create User Modal */}
      {showCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl"
            style={{ 
              position: 'relative',
              maxHeight: '90vh',
              overflow: 'auto',
              margin: '20px'
            }}
          >
            <h2 className="text-xl font-bold mb-4">Create New User</h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="First Name"
                value={newUser.firstName}
                onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <input
                type="text"
                placeholder="Last Name"
                value={newUser.lastName}
                onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <input
                type="email"
                placeholder="Email Address"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <textarea
                placeholder="Admin Notes (optional)"
                value={newUser.adminNotes}
                onChange={(e) => setNewUser({...newUser, adminNotes: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={isGenerating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isGenerating ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agreement Details Modal */}
      {showAgreementModal && selectedUserAgreement && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-xl"
            style={{ 
              position: 'relative',
              maxHeight: '80vh',
              overflow: 'auto',
              margin: '20px'
            }}
          >
            <h2 className="text-xl font-bold mb-4">Agreement Details</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">User Information</h3>
                <p>{selectedUserAgreement.user.firstName} {selectedUserAgreement.user.lastName}</p>
                <p className="text-gray-600">{selectedUserAgreement.user.email}</p>
              </div>
              <div>
                <h3 className="font-semibold">Agreement Status</h3>
                <p className={selectedUserAgreement.signature.signed ? 'text-green-600' : 'text-red-600'}>
                  {selectedUserAgreement.signature.signed
                    ? <span className="inline-flex items-center gap-1"><Check size={14} />Accepted</span>
                    : <span className="inline-flex items-center gap-1"><X size={14} />Not Accepted</span>}
                </p>
                {selectedUserAgreement.signature.signatureDate && (
                  <p className="text-gray-600">
                    Signed: {new Date(selectedUserAgreement.signature.signatureDate).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAgreementModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          isOpen={showUserDetail}
          onClose={() => {
            setShowUserDetail(false);
            setSelectedUserId(null);
          }}
          onUserUpdated={() => {
            loadAllUsers();
          }}
          onDeleteUserData={(userId, userEmail, userName) => {
            // Close detail modal and open deletion modal
            setShowUserDetail(false);
            setDeletionUser({ id: userId, email: userEmail, name: userName });
            setShowDeletionModal(true);
          }}
        />
      )}

      {/* User Deletion Modal */}
      {deletionUser && (
        <UserDeletionModal
          isOpen={showDeletionModal}
          onClose={() => {
            setShowDeletionModal(false);
            setDeletionUser(null);
          }}
          userId={deletionUser.id}
          userEmail={deletionUser.email}
          userName={deletionUser.name}
          onDeletionComplete={() => {
            setShowDeletionModal(false);
            setDeletionUser(null);
            setSelectedUserId(null);
            loadAllUsers();
          }}
        />
      )}

      {/* Add Waitlist Entry Modal */}
      {showAddWaitlist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Waitlist Entry</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={newWaitlistEntry.firstName}
                  onChange={e => setNewWaitlistEntry(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={newWaitlistEntry.lastName}
                  onChange={e => setNewWaitlistEntry(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={newWaitlistEntry.email}
                  onChange={e => setNewWaitlistEntry(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                <textarea
                  value={newWaitlistEntry.adminNotes}
                  onChange={e => setNewWaitlistEntry(prev => ({ ...prev, adminNotes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Optional notes..."
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select
                  value={newWaitlistEntry.signupSource}
                  onChange={e => setNewWaitlistEntry(prev => ({ ...prev, signupSource: e.target.value as 'manual' }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="manual">Manual (Admin)</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => {
                  setShowAddWaitlist(false);
                  setNewWaitlistEntry({ firstName: '', lastName: '', email: '', adminNotes: '', signupSource: 'manual' });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={addWaitlistEntry}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                Add to Waitlist
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Waitlist Modal */}
      {showBulkDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Waitlist Entries</h3>
            <p className="text-sm text-gray-600 mb-5">
              Are you sure you want to delete <strong>{selectedEntries.size}</strong> waitlist{' '}
              {selectedEntries.size === 1 ? 'entry' : 'entries'}? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkDelete(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={bulkDeleteWaitlistEntries}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete {selectedEntries.size} {selectedEntries.size === 1 ? 'Entry' : 'Entries'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Graduate (Promote) Auth Method Modal */}
      {promoteModalEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Graduate Waitlist User</h3>
            <p className="text-sm text-gray-500 mb-4">
              Graduating <strong>{promoteModalEntry.firstName} {promoteModalEntry.lastName}</strong> ({promoteModalEntry.email})
            </p>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Creation Method</label>
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 has-[:checked]:border-green-500 has-[:checked]:bg-green-50">
                  <input
                    type="radio"
                    name="authMethod"
                    value="invite"
                    checked={promoteAuthMethod === 'invite'}
                    onChange={() => setPromoteAuthMethod('invite')}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Send Invite Link</div>
                    <div className="text-xs text-gray-500">User receives an email with a magic link to set up their account. Works for any email address.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 has-[:checked]:border-green-500 has-[:checked]:bg-green-50">
                  <input
                    type="radio"
                    name="authMethod"
                    value="google"
                    checked={promoteAuthMethod === 'google'}
                    onChange={() => setPromoteAuthMethod('google')}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Google Sign-In</div>
                    <div className="text-xs text-gray-500">User signs in with their Google account. Best for Gmail addresses.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 has-[:checked]:border-green-500 has-[:checked]:bg-green-50">
                  <input
                    type="radio"
                    name="authMethod"
                    value="email"
                    checked={promoteAuthMethod === 'email'}
                    onChange={() => setPromoteAuthMethod('email')}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Email & Password</div>
                    <div className="text-xs text-gray-500">User receives a temporary password by email and can change it after login.</div>
                  </div>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPromoteModalEntry(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmPromoteWaitlistEntry}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                <GraduationCap size={16} style={{ display: 'inline', verticalAlign: '-3px', marginRight: 4 }} />Graduate User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Graduate (Promote) Waitlist Modal */}
      {showBulkPromote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Graduate Waitlist Entries</h3>
            <p className="text-sm text-gray-600 mb-5">
              Graduate <strong>{selectedEntries.size}</strong> waitlist{' '}
              {selectedEntries.size === 1 ? 'entry' : 'entries'} to active users? They will each
              receive a welcome email with login instructions.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkPromote(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={bulkPromoteWaitlistEntries}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                <GraduationCap size={16} style={{ display: 'inline', verticalAlign: '-3px', marginRight: 4 }} />Graduate {selectedEntries.size} {selectedEntries.size === 1 ? 'User' : 'Users'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Evaluation Grant / Extend / Revoke Multi-Step Modal ─── */}
      {evalModalMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget && !evalLoading) closeEvalModal(); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" style={{ maxHeight: '90vh', overflow: 'auto' }}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {evalModalMode === 'grant' && 'Extend Free Trial'}
                    {evalModalMode === 'extend' && 'Add More Time to Trial Extension'}
                    {evalModalMode === 'revoke' && 'End Free Trial Extension'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">{evalUserName} ({evalUserEmail})</p>
                </div>
                <button onClick={closeEvalModal} disabled={evalLoading} className="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Close">&times;</button>
              </div>
              {/* Step indicator */}
              {evalStep !== 'result' && (
                <div className="flex items-center gap-1.5 mt-3">
                  {Array.from({ length: evalTotalSteps() }, (_, i) => (
                    <div key={i} className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: i < evalStepIndex() ? 'var(--status-success-text)' : 'var(--border)' }} />
                  ))}
                  <span className="text-xs text-gray-400 ml-2">Step {evalStepIndex()}/{evalTotalSteps()}</span>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {/* Error banner */}
              {evalError && (
                <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--status-error-bg)', color: 'var(--status-error-text)', border: '1px solid var(--status-error-bg)' }}>
                  {evalError}
                  {evalStep !== 'result' && evalStep !== 'confirm' && (
                    <button onClick={() => setEvalError('')} className="ml-2 underline text-xs">Dismiss</button>
                  )}
                </div>
              )}

              {/* Step 1: Select Reason (grant only) */}
              {evalStep === 'reason' && evalModalMode === 'grant' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">Why is this user receiving a free trial extension?</p>
                  <div className="space-y-2">
                    {EVALUATION_REASON_OPTIONS.map((r) => (
                      <button
                        key={r.key}
                        onClick={() => evalSelectReason(r.key)}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-green-500 hover:bg-green-50 transition-colors"
                      >
                        <div className="text-sm font-medium text-gray-900">{r.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{r.description}</div>
                        <div className="text-xs text-gray-400 mt-1">Default: {EVALUATION_DURATION_OPTIONS[r.defaultDuration]}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Select Duration (grant + extend) */}
              {evalStep === 'duration' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-600">
                      {evalModalMode === 'grant' && <>Select trial extension duration for <strong>{EVALUATION_REASON_OPTIONS.find(r => r.key === evalReason)?.label}</strong></>}
                      {evalModalMode === 'extend' && 'Add more time to extension:'}
                    </p>
                    {evalModalMode === 'grant' && (
                      <button onClick={evalGoBack} className="text-xs text-gray-500 hover:text-gray-700">&larr; Back</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(EVALUATION_DURATION_OPTIONS).map(([key, label]) => {
                      const isDefault = evalModalMode === 'grant' && key === (EVALUATION_REASON_OPTIONS.find(r => r.key === evalReason)?.defaultDuration);
                      return (
                        <button
                          key={key}
                          onClick={() => evalSelectDuration(key)}
                          className="p-3 rounded-lg border text-sm text-left transition-colors"
                          style={{
                            borderColor: isDefault ? 'var(--status-success-text)' : 'var(--border)',
                            backgroundColor: isDefault ? 'var(--status-success-bg)' : 'white',
                          }}
                        >
                          <span className="font-medium">{label}</span>
                          {isDefault && <span className="text-xs text-green-700 ml-1">(recommended)</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 3: Preflight / Impact Preview (grant only) */}
              {evalStep === 'preflight' && evalModalMode === 'grant' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm font-medium text-gray-700">Eligibility & Impact Preview</p>
                    <button onClick={evalGoBack} className="text-xs text-gray-500 hover:text-gray-700">&larr; Back</button>
                  </div>
                  {evalLoading ? (
                    <div className="flex items-center justify-center py-8 text-sm text-gray-500">Checking eligibility...</div>
                  ) : evalPreflight ? (
                    <div className="space-y-3">
                      {/* Eligibility */}
                      <div className="p-3 rounded-lg text-sm" style={{
                        backgroundColor: evalPreflight.eligible ? 'var(--status-success-bg)' : 'var(--status-error-bg)',
                        border: `1px solid ${evalPreflight.eligible ? 'var(--status-success-bg)' : 'var(--status-error-bg)'}`,
                      }}>
                        <div className="font-medium" style={{ color: evalPreflight.eligible ? 'var(--status-success-text)' : 'var(--status-error-text)' }}>
                          {evalPreflight.eligible
                            ? <span className="inline-flex items-center gap-1"><Check size={14} />Eligible</span>
                            : <span className="inline-flex items-center gap-1"><X size={14} />Not Eligible</span>}
                        </div>
                        <div className="text-xs mt-1" style={{ color: evalPreflight.eligible ? 'var(--status-success-text)' : 'var(--status-error-text)' }}>
                          {evalPreflight.eligibilityNote}
                        </div>
                      </div>

                      {evalPreflight.eligible && (
                        <>
                          {/* Impact summary */}
                          <div className="border rounded-lg divide-y">
                            <div className="px-3 py-2 flex justify-between text-sm">
                              <span className="text-gray-500">Current tier</span>
                              <span className="font-medium">{evalPreflight.currentTier} ({evalPreflight.currentStatus})</span>
                            </div>
                            <div className="px-3 py-2 flex justify-between text-sm">
                              <span className="text-gray-500">Will change to</span>
                              <span className="font-medium text-green-700">{evalPreflight.willChangeTo}</span>
                            </div>
                            <div className="px-3 py-2 flex justify-between text-sm">
                              <span className="text-gray-500">Reason</span>
                              <span className="font-medium">{evalPreflight.reason}</span>
                            </div>
                            <div className="px-3 py-2 flex justify-between text-sm">
                              <span className="text-gray-500">Duration</span>
                              <span className="font-medium">{evalPreflight.duration}</span>
                            </div>
                            {evalPreflight.deferredStart && (
                              <div className="px-3 py-2 flex justify-between text-sm">
                                <span className="text-gray-500">Start</span>
                                <span className="font-medium text-amber-600">Deferred (after trial ends)</span>
                              </div>
                            )}
                          </div>

                          {/* Side effects */}
                          <div className="text-xs text-gray-500 space-y-1">
                            <div>
                              Campaign trigger: <code className="bg-gray-100 px-1 rounded">{evalPreflight.campaignTrigger}</code>
                              {evalPreflight.campaignReady ? (
                                <span className="text-green-600 ml-1">({evalPreflight.campaignStepCount} step{evalPreflight.campaignStepCount !== 1 ? 's' : ''} ready)</span>
                              ) : null}
                            </div>
                            <div>Email notification will be sent to the user</div>
                            <div>RevenueCat promotional entitlement will be granted</div>
                            <div>Audit log entry will be created in subscription_events</div>
                          </div>

                          {/* Campaign-not-active warning banner (shown prominently when relevant) */}
                          {!evalPreflight.campaignReady && (
                            <div className="p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--note-bg)', border: '1px solid var(--status-warning-bg)' }}>
                              <div className="font-medium text-amber-800 mb-1 flex items-center gap-1"><AlertTriangle size={14} />In-app announcement won't fire</div>
                              <div className="text-xs text-amber-700">
                                The campaign for this reason isn't active. The user will receive the email only.
                                Activate it in <a href="/admin/campaigns" className="underline">/admin/campaigns</a> if you want the in-app message.
                              </div>
                            </div>
                          )}

                          <button
                            onClick={() => { setEvalError(''); setEvalStep('confirm'); }}
                            className="w-full mt-2 px-4 py-2.5 text-sm font-medium text-white rounded-lg"
                            style={{ backgroundColor: 'var(--status-success-text)' }}
                          >
                            Continue to Confirmation
                          </button>
                        </>
                      )}

                      {!evalPreflight.eligible && (
                        <button onClick={closeEvalModal} className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                          Close
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Step 4: Confirmation */}
              {evalStep === 'confirm' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm font-medium text-gray-700">
                      {evalModalMode === 'revoke' ? 'Confirm Revocation' : 'Confirm Action'}
                    </p>
                    {evalModalMode !== 'revoke' && (
                      <button onClick={evalGoBack} className="text-xs text-gray-500 hover:text-gray-700">&larr; Back</button>
                    )}
                  </div>

                  {/* Warning box */}
                  <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'var(--note-bg)', border: '1px solid var(--status-warning-bg)' }}>
                    <div className="text-sm font-medium text-amber-800 mb-2">This action is irreversible</div>
                    <div className="text-sm text-amber-700 space-y-1">
                      {evalModalMode === 'grant' && (
                        <>
                          <div><strong>User:</strong> {evalUserName} ({evalUserEmail})</div>
                          <div><strong>Action:</strong> Extend free trial by {EVALUATION_DURATION_OPTIONS[evalDuration]}</div>
                          <div><strong>Reason:</strong> {EVALUATION_REASON_OPTIONS.find(r => r.key === evalReason)?.label}</div>
                          <div className="text-xs mt-2">This will send an email with cancellation instructions and fire an in-app campaign.</div>
                        </>
                      )}
                      {evalModalMode === 'extend' && (
                        <>
                          <div><strong>User:</strong> {evalUserName} ({evalUserEmail})</div>
                          <div><strong>Action:</strong> Add {EVALUATION_DURATION_OPTIONS[evalDuration]} to existing free trial extension</div>
                        </>
                      )}
                      {evalModalMode === 'revoke' && (
                        <>
                          <div><strong>User:</strong> {evalUserName} ({evalUserEmail})</div>
                          <div><strong>Action:</strong> End the free trial extension and restore pre-extension subscription state</div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={closeEvalModal}
                      disabled={evalLoading}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (evalModalMode === 'grant') executeGrant();
                        else if (evalModalMode === 'extend') executeExtend();
                        else if (evalModalMode === 'revoke') executeRevoke();
                      }}
                      disabled={evalLoading}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg"
                      style={{
                        backgroundColor: evalModalMode === 'revoke' ? 'var(--status-error)' : 'var(--status-success-text)',
                        opacity: evalLoading ? 0.6 : 1,
                        cursor: evalLoading ? 'wait' : 'pointer',
                      }}
                    >
                      {evalLoading ? 'Processing...' : (
                        evalModalMode === 'grant' ? 'Extend Free Trial' :
                        evalModalMode === 'extend' ? 'Add Time' :
                        'End Trial Extension'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 5: Result */}
              {evalStep === 'result' && (
                <div>
                  {evalResult?.success ? (
                    <div className="text-center py-4">
                      <div className="mb-3 flex justify-center">{evalModalMode === 'revoke' ? <RefreshCw size={30} className="text-amber-500" /> : <Check size={30} className="text-green-600" />}</div>
                      <div className="text-lg font-semibold text-gray-900 mb-1">
                        {evalModalMode === 'grant' && 'Free Trial Extended'}
                        {evalModalMode === 'extend' && 'Extension Lengthened'}
                        {evalModalMode === 'revoke' && 'Trial Extension Ended'}
                      </div>
                      <div className="text-sm text-gray-600 mb-4">
                        {evalModalMode === 'grant' && `${evalUserName} now has Pro access for ${EVALUATION_DURATION_OPTIONS[evalDuration]}`}
                        {evalModalMode === 'extend' && `${evalUserName}'s free trial extension lengthened by ${EVALUATION_DURATION_OPTIONS[evalDuration]}`}
                        {evalModalMode === 'revoke' && `${evalUserName} restored to ${evalResult.restored_tier || 'previous'} tier`}
                      </div>
                      {evalResult.evaluation_type && (
                        <div className="text-xs text-gray-400 mb-1">Type: {evalResult.evaluation_type}</div>
                      )}
                      {evalResult.campaign_triggered !== undefined && (
                        <div className="text-xs text-gray-400 mb-1">Campaign triggered: {evalResult.campaign_triggered ? 'Yes' : 'No'}</div>
                      )}
                      <div className="text-xs text-gray-400">Audit log entry created</div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="mb-3 flex justify-center"><X size={30} className="text-red-600" /></div>
                      <div className="text-lg font-semibold text-red-700 mb-1">Operation Failed</div>
                      <div className="text-sm text-gray-600 mb-4">{evalError || 'An unexpected error occurred'}</div>
                      <button
                        onClick={() => {
                          setEvalError('');
                          setEvalStep('confirm');
                        }}
                        className="text-sm text-green-700 underline hover:text-green-900"
                      >
                        Try Again
                      </button>
                    </div>
                  )}
                  <button
                    onClick={closeEvalModal}
                    className="w-full mt-4 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
